/**
 * Beautify and minify HTML without changing how it renders.
 *
 * HTML is not XML: whitespace between two inline elements is a visible space, so a
 * formatter that puts every tag on its own line changes the page. The rules here only
 * add or remove whitespace where a browser would collapse it anyway — around block-level
 * elements, between the children of containers like `<select>` and `<svg>` — and
 * otherwise only re-shape whitespace that is already there (a space may become a line
 * break and back). `pre`, `textarea`, `script` and `style` are copied exactly.
 */

import {
  type ElementNode,
  type HtmlIssue,
  type HtmlNode,
  parseHtmlDocument,
  VOID_ELEMENTS,
} from "@/lib/tool-kit/developer/html/tree";

/** Elements a browser lays out as blocks: whitespace around them is not rendered. */
const BLOCK_ELEMENTS = new Set([
  "address",
  "article",
  "aside",
  "base",
  "blockquote",
  "body",
  "caption",
  "col",
  "colgroup",
  "dd",
  "details",
  "dialog",
  "dir",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "frameset",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hgroup",
  "hr",
  "html",
  "iframe",
  "legend",
  "li",
  "link",
  "main",
  "menu",
  "meta",
  "nav",
  "noscript",
  "ol",
  "optgroup",
  "option",
  "p",
  "pre",
  "script",
  "search",
  "section",
  "style",
  "summary",
  "table",
  "tbody",
  "td",
  "template",
  "tfoot",
  "th",
  "thead",
  "title",
  "tr",
  "ul",
]);

/** Content copied byte for byte: whitespace here is code, or is displayed as typed. */
const PRESERVED = new Set([
  "iframe",
  "listing",
  "noembed",
  "noframes",
  "noscript",
  "plaintext",
  "pre",
  "script",
  "style",
  "textarea",
  "xmp",
]);

/** Inline elements whose children are never laid out as text. */
const LAYOUT_CONTAINERS = new Set([
  "audio",
  "datalist",
  "map",
  "math",
  "object",
  "picture",
  "select",
  "svg",
  "video",
]);

const RE_WHITESPACE_SPLIT = /(\s+)/;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_ONLY_WHITESPACE = /^\s*$/;
const RE_ATTRIBUTE_EQUALS = /^([^\s=]+)\s*=\s*([\s\S]*)$/;
const RE_TRAILING_LINE_SPACE = /\n[ \t]*$/;
const RE_LINE_ENDINGS = /\r\n?/g;
const RE_CONDITIONAL_COMMENT = /^<!--\[if|<!\[endif\]-->$/;
/** Elements nested deeper than this are copied from the source as they are. */
const MAX_DEPTH = 256;

export type AttributeWrap = "never" | "auto" | "always";

export interface BeautifyOptions {
  /** Two spaces, four spaces, or a tab. */
  indent: string;
  /** Put a block element's attributes one per line: never, past `lineLength`, always (2+). */
  wrapAttributes: AttributeWrap;
  lineLength: number;
}

export interface MinifyOptions {
  /** Remove comments, except Internet Explorer / Outlook conditional comments. */
  removeComments: boolean;
}

export interface FormatResult {
  output: string;
  issues: HtmlIssue[];
  elements: number;
}

function isElement(node: HtmlNode): node is ElementNode {
  return node.kind === "element";
}

function isWhitespaceText(node: HtmlNode): boolean {
  return node.kind === "text" && RE_ONLY_WHITESPACE.test(node.raw);
}

function countElements(node: ElementNode): number {
  let count = 0;
  const pending: ElementNode[] = [node];
  while (pending.length > 0) {
    const current = pending.pop() as ElementNode;
    for (const child of current.children) {
      if (isElement(child)) {
        count += 1;
        pending.push(child);
      }
    }
  }
  return count;
}

const blockDescendantCache = new WeakMap<ElementNode, boolean>();

/** Whether any descendant is block-level — computed bottom-up, without recursion. */
function hasBlockDescendant(node: ElementNode): boolean {
  const pending: [ElementNode, boolean][] = [[node, false]];
  while (pending.length > 0) {
    const [current, expanded] = pending.pop() as [ElementNode, boolean];
    if (blockDescendantCache.has(current)) {
      continue;
    }
    if (!expanded) {
      pending.push([current, true]);
      for (const child of current.children) {
        if (isElement(child) && !blockDescendantCache.has(child)) {
          pending.push([child, false]);
        }
      }
      continue;
    }
    blockDescendantCache.set(
      current,
      current.children.some(
        (child) =>
          isElement(child) &&
          (BLOCK_ELEMENTS.has(child.name) ||
            blockDescendantCache.get(child) === true)
      )
    );
  }
  return blockDescendantCache.get(node) ?? false;
}

/** Accumulates one inline run as lines, tracking the last character it wrote. */
class LineBuilder {
  readonly lines: string[] = [];
  parts: string[] = [];
  last = "";

  append(text: string): void {
    if (text.length > 0) {
      this.parts.push(text);
      this.last = text.at(-1) ?? "";
    }
  }

  /** One space between words — never at the start of a line, never doubled. */
  space(): void {
    if (this.parts.length > 0 && this.last !== " ") {
      this.append(" ");
    }
  }

  newline(): void {
    if (this.parts.length > 0) {
      this.lines.push(this.parts.join("").trim());
      this.parts = [];
      this.last = "";
    }
  }

  finish(): string[] {
    this.newline();
    return this.lines.filter((line) => line !== "");
  }
}

/** Children of `<select>`, `<svg>` and the like are laid out as boxes, never as text. */
function isLayoutContainer(node: ElementNode): boolean {
  if (!(LAYOUT_CONTAINERS.has(node.name) || node.foreign)) {
    return false;
  }
  return node.children.every(
    (child) => child.kind !== "text" || RE_ONLY_WHITESPACE.test(child.raw)
  );
}

type Segment =
  | { kind: "block"; node: HtmlNode }
  | { kind: "run"; nodes: HtmlNode[] };

/**
 * Split children into block-level nodes and runs of inline content. A run holding
 * nothing but one inline element that wraps blocks (an `<a>` around a card) is promoted
 * to a block: with only whitespace beside it, its edges are block boundaries too.
 */
function segmentize(nodes: HtmlNode[]): Segment[] {
  const segments: Segment[] = [];
  let run: HtmlNode[] = [];
  const flush = () => {
    if (run.length === 0) {
      return;
    }
    const solid = run.filter((node) => !isWhitespaceText(node));
    const only = solid.length === 1 ? solid[0] : null;
    if (only && isElement(only) && hasBlockDescendant(only)) {
      segments.push({ kind: "block", node: only });
    } else {
      segments.push({ kind: "run", nodes: run });
    }
    run = [];
  };
  for (const node of nodes) {
    const block =
      node.kind === "declaration" ||
      (isElement(node) && BLOCK_ELEMENTS.has(node.name));
    if (block) {
      flush();
      segments.push({ kind: "block", node });
    } else {
      run.push(node);
    }
  }
  flush();
  return segments;
}

/** `name = "value"` → `name="value"`: the value keeps its own quotes and content. */
function attributeText(raw: string): string {
  const match = RE_ATTRIBUTE_EQUALS.exec(raw);
  return match ? `${match[1]}=${match[2]}` : raw;
}

function endTag(node: ElementNode): string {
  return node.hasEndTag ? `</${node.sourceName}>` : "";
}

/** Words go on the current line; whitespace that held a line break starts a new one. */
function appendText(raw: string, builder: LineBuilder): void {
  for (const piece of raw.split(RE_WHITESPACE_SPLIT)) {
    if (piece === "") {
      continue;
    }
    if (!RE_ONLY_WHITESPACE.test(piece)) {
      builder.append(piece);
    } else if (piece.includes("\n")) {
      builder.newline();
    } else {
      builder.space();
    }
  }
}

class Printer {
  readonly input: string;

  constructor(input: string) {
    this.input = input;
  }

  inner(node: ElementNode): string {
    return this.input.slice(node.innerStart, node.innerEnd);
  }

  /** The element exactly as written. */
  source(node: ElementNode): string {
    return this.input.slice(node.start, node.end).trim();
  }

  startTag(node: ElementNode, separator = " "): string {
    const attrs = node.attrs.map((attr) => attributeText(attr.raw));
    const body = attrs.length > 0 ? `${separator}${attrs.join(separator)}` : "";
    return `<${node.sourceName}${body}${node.selfClosing ? " /" : ""}>`;
  }
}

class Beautifier extends Printer {
  readonly options: BeautifyOptions;
  readonly lines: string[] = [];

  constructor(input: string, options: BeautifyOptions) {
    super(input);
    this.options = options;
  }

  pad(depth: number): string {
    return this.options.indent.repeat(depth);
  }

  /** A block element's start tag, with its attributes wrapped one per line if asked. */
  blockStartTag(node: ElementNode, depth: number): string {
    const single = this.startTag(node);
    const { wrapAttributes, lineLength } = this.options;
    const wrap =
      node.attrs.length > 1 &&
      (wrapAttributes === "always" ||
        (wrapAttributes === "auto" &&
          this.pad(depth).length + single.length > lineLength));
    if (!wrap) {
      return single;
    }
    const inner = `\n${this.pad(depth + 1)}`;
    const attrs = node.attrs.map(
      (attr) => `${inner}${attributeText(attr.raw)}`
    );
    return `<${node.sourceName}${attrs.join("")}\n${this.pad(depth)}${node.selfClosing ? "/>" : ">"}`;
  }

  children(nodes: HtmlNode[], depth: number): void {
    for (const segment of segmentize(nodes)) {
      if (segment.kind === "block") {
        this.block(segment.node, depth);
      } else {
        for (const line of this.runLines(segment.nodes, depth)) {
          this.lines.push(`${this.pad(depth)}${line}`);
        }
      }
    }
  }

  block(node: HtmlNode, depth: number): void {
    const pad = this.pad(depth);
    if (!isElement(node)) {
      this.lines.push(`${pad}${node.raw.trim()}`);
      return;
    }
    if (depth > MAX_DEPTH) {
      this.lines.push(`${pad}${this.source(node)}`);
      return;
    }
    const open = this.blockStartTag(node, depth);
    if (
      VOID_ELEMENTS.has(node.name) ||
      (node.selfClosing && node.children.length === 0)
    ) {
      this.lines.push(`${pad}${open}`);
      return;
    }
    if (PRESERVED.has(node.name)) {
      this.lines.push(`${pad}${this.preserved(node, open, pad)}`);
      return;
    }
    const close = endTag(node);
    if (segmentize(node.children).some((segment) => segment.kind === "block")) {
      this.lines.push(`${pad}${open}`);
      this.children(node.children, depth + 1);
      if (close) {
        this.lines.push(`${pad}${close}`);
      }
      return;
    }
    const lines = this.runLines(node.children, depth + 1);
    if (
      lines.length <= 1 &&
      !open.includes("\n") &&
      !(lines[0] ?? "").includes("\n")
    ) {
      this.lines.push(`${pad}${open}${lines[0] ?? ""}${close}`);
      return;
    }
    this.lines.push(`${pad}${open}`);
    for (const line of lines) {
      this.lines.push(`${this.pad(depth + 1)}${line}`);
    }
    if (close) {
      this.lines.push(`${pad}${close}`);
    }
  }

  /** Script and style: content untouched, the end tag lined up with the start tag. */
  preserved(node: ElementNode, open: string, pad: string): string {
    const inner = this.inner(node);
    const close = endTag(node);
    const code = node.name === "script" || node.name === "style";
    if (code && RE_ONLY_WHITESPACE.test(inner)) {
      return `${open}${close}`;
    }
    if (code && inner.includes("\n")) {
      return `${open}${inner.replace(RE_TRAILING_LINE_SPACE, "\n")}${pad}${close}`;
    }
    return `${open}${inner}${close}`;
  }

  /** A run of inline content as lines, breaking only where the source had whitespace. */
  runLines(nodes: HtmlNode[], depth: number): string[] {
    const builder = new LineBuilder();
    for (const node of nodes) {
      if (node.kind === "text") {
        appendText(node.raw, builder);
      } else if (isElement(node)) {
        builder.append(this.inlineItem(node, depth));
        if (node.name === "br") {
          builder.newline();
        }
      } else {
        builder.append(node.raw);
      }
    }
    return builder.finish();
  }

  /** An inline element on one line — or, for `<select>`/`<svg>`, its children indented. */
  inlineItem(node: ElementNode, depth: number): string {
    if (depth > MAX_DEPTH) {
      return this.source(node);
    }
    const open = this.startTag(node);
    if (
      VOID_ELEMENTS.has(node.name) ||
      (node.selfClosing && node.children.length === 0)
    ) {
      return open;
    }
    if (PRESERVED.has(node.name)) {
      return `${open}${this.inner(node)}${endTag(node)}`;
    }
    const solid = node.children.filter((child) => !isWhitespaceText(child));
    if (isLayoutContainer(node) && solid.some(isElement)) {
      const inner = this.pad(depth + 1);
      const items = solid.map((child) =>
        isElement(child) ? this.inlineItem(child, depth + 1) : child.raw.trim()
      );
      return `${open}\n${inner}${items.join(`\n${inner}`)}\n${this.pad(depth)}${endTag(node)}`;
    }
    return `${open}${this.inlineContent(node.children, depth)}${endTag(node)}`;
  }

  inlineContent(nodes: HtmlNode[], depth: number): string {
    let out = "";
    for (const node of nodes) {
      if (node.kind === "text") {
        out += node.raw.replace(RE_WHITESPACE_RUN, " ");
      } else if (isElement(node)) {
        out += this.inlineItem(node, depth + 1);
      } else {
        out += node.raw;
      }
    }
    return out;
  }
}

class Minifier extends Printer {
  readonly options: MinifyOptions;

  constructor(input: string, options: MinifyOptions) {
    super(input);
    this.options = options;
  }

  keepComment(raw: string): boolean {
    return !this.options.removeComments || RE_CONDITIONAL_COMMENT.test(raw);
  }

  children(nodes: HtmlNode[], blockParent: boolean, depth: number): string {
    const segments = segmentize(nodes);
    let out = "";
    for (const [index, segment] of segments.entries()) {
      if (segment.kind === "block") {
        out += this.block(segment.node, depth);
        continue;
      }
      let text = this.run(segment.nodes, depth);
      if (index > 0 || blockParent) {
        text = text.trimStart();
      }
      if (index < segments.length - 1 || blockParent) {
        text = text.trimEnd();
      }
      out += text;
    }
    return out;
  }

  block(node: HtmlNode, depth: number): string {
    if (!isElement(node)) {
      return node.raw.trim();
    }
    if (depth > MAX_DEPTH) {
      return this.source(node);
    }
    const open = this.startTag(node);
    if (
      VOID_ELEMENTS.has(node.name) ||
      (node.selfClosing && node.children.length === 0)
    ) {
      return open;
    }
    if (PRESERVED.has(node.name)) {
      return `${open}${this.preserved(node)}${endTag(node)}`;
    }
    return `${open}${this.children(node.children, true, depth + 1)}${endTag(node)}`;
  }

  preserved(node: ElementNode): string {
    const inner = this.inner(node);
    return node.name === "script" || node.name === "style"
      ? inner.trim()
      : inner;
  }

  /** Inline content with every whitespace run collapsed to one space. */
  run(nodes: HtmlNode[], depth: number): string {
    const parts: string[] = [];
    let last = "";
    for (const node of nodes) {
      let piece = "";
      if (node.kind === "text") {
        piece = node.raw.replace(RE_WHITESPACE_RUN, " ");
      } else if (isElement(node)) {
        piece = this.inline(node, depth + 1);
      } else if (node.kind !== "comment" || this.keepComment(node.raw)) {
        piece = node.raw;
      }
      if (last === " " && piece.startsWith(" ")) {
        piece = piece.slice(1);
      }
      if (piece.length > 0) {
        parts.push(piece);
        last = piece.at(-1) ?? "";
      }
    }
    return parts.join("");
  }

  inline(node: ElementNode, depth: number): string {
    if (depth > MAX_DEPTH) {
      return this.source(node);
    }
    const open = this.startTag(node);
    if (
      VOID_ELEMENTS.has(node.name) ||
      (node.selfClosing && node.children.length === 0)
    ) {
      return open;
    }
    if (PRESERVED.has(node.name)) {
      return `${open}${this.preserved(node)}${endTag(node)}`;
    }
    const children = isLayoutContainer(node)
      ? node.children.filter((child) => !isWhitespaceText(child))
      : node.children;
    return `${open}${this.run(children, depth)}${endTag(node)}`;
  }
}

export function beautifyHtml(
  input: string,
  options: BeautifyOptions
): FormatResult {
  const source = input.replace(RE_LINE_ENDINGS, "\n");
  const { root, issues } = parseHtmlDocument(source);
  const printer = new Beautifier(source, options);
  printer.children(root.children, 0);
  return {
    output: printer.lines.length > 0 ? `${printer.lines.join("\n")}\n` : "",
    issues,
    elements: countElements(root),
  };
}

export function minifyHtml(
  input: string,
  options: MinifyOptions
): FormatResult {
  const source = input.replace(RE_LINE_ENDINGS, "\n");
  const { root, issues } = parseHtmlDocument(source);
  return {
    output: new Minifier(source, options).children(root.children, true, 0),
    issues,
    elements: countElements(root),
  };
}
